import { createContext } from 'react';

export enum ThemeMode {
  AUTO = 'auto',
  LIGHT = 'light',
  DARK = 'dark',
}

const initialThemeMode = getLocalThemeMode();

export type ThemeContextValue = ReturnType<typeof useThemeContextValue>;

export const themeContext = createContext<ThemeContextValue>({} as never);

export function useThemeContextValue() {
  const [themeModeValue, setThemeModeValue] = useState<ThemeMode>(initialThemeMode);
  const [isSystemDark, setIsSystemDark] = useState(false);

  const themeMode = useMemo(() => {
    switch (themeModeValue) {
      case ThemeMode.AUTO:
        return isSystemDark ? ThemeMode.DARK : ThemeMode.LIGHT;
      default:
        return themeModeValue;
    }
  }, [themeModeValue, isSystemDark]);

  const isThemeMode = useCallback((mode: ThemeMode) => mode === themeMode, [themeMode]);

  const isDark = useMemo(() => isThemeMode(ThemeMode.DARK), [isThemeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      themeModeValue,
      setThemeModeValue,
      isThemeMode,
      isDark,
    }),
    [themeMode, themeModeValue, isThemeMode, isDark]
  );

  // Set the theme mode based on the system preference
  useLayoutEffect(() => {
    const m = window.matchMedia('(prefers-color-scheme: dark)');

    m.addEventListener('change', handleThemeModeChange);
    m.dispatchEvent(new Event('change'));

    function handleThemeModeChange() {
      setIsSystemDark(m.matches);
    }

    return () => {
      m.removeEventListener('change', handleThemeModeChange);
    };
  }, []);

  useLayoutEffect(() => {
    setLocalThemeMode(themeModeValue);
  }, [themeModeValue]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = themeMode;
  }, [themeMode]);

  return value;
}

export function useThemeContext() {
  return useContext(themeContext);
}

export function getLocalThemeMode() {
  return (localStorage.getItem('theme-mode') as ThemeMode) ?? ThemeMode.AUTO;
}

export function setLocalThemeMode(mode: ThemeMode) {
  return localStorage.setItem('theme-mode', mode);
}
